/**
 * NOTE: This file needs to be kept in sync with the non-SystemJS variant:
 * public/static/js/quickhire/module-quickhire.js
 * Please replicate all changes to the other file.
 */

import jq from 'jquery';
import _ from 'underscore';
import 'jquery-validate';
import { oneLine } from 'common-tags';
import { getUserIdfromCookie, getCSRFToken } from '@shared/cookie';
import { T_ } from '@shared/translation';
import { qtsp } from '@shared/tracking-qtsbinds';
import { cdn_url } from '@shared/cdn';
import uiIcon from '@shared/ui-icon';
import paymentSource from '@shared/modal-payments-source';
import awardUpgrades from '@shared/modal-project-award-upgrades';
import Modal from '@shared/ui-modal';
import { ABTestApi } from '@shared/abtest-api';
import fileupload from './project/postproject-fileupload.js';
import userAuthForm from './users/user_auth_form.js';
import hiremeModalContainerTemplate from './templates/container.html';
import quickHireFormTemplate from './templates/form.html';
import quickHireFormTemplateHeader from './templates/header.html';
import quickHireHourlyFormTemplateHeader from './templates/hourly-header.html';
import quickHireMilestoneFormTemplate from './templates/milestone-form.html';
import hourlyFormTemplate from './templates/hourly-form.html';
import 'css/flux/legacy/quickhire-modal.less';
import 'css/flux/layout/milestone-modal.less';

/**
 * Warning: This module is used on ALL bootstrap pages across the site.
 * Please be considerate when adding selectors, and use the
 * quickhireFormContainerDom variable, private to this module, which refers to
 * the container element for this modal to find the elements you are looking
 * for. Otherwise this module may misbehave on other pages.
 */

// The module must have a container with class 'quick-hire-form-container'
let budgetsCache = [];
let durationsCache = [];
let feeNotes = false;
let quickHireReturnData = {};
let thisUser = getUserIdfromCookie();
let quickhireFormContainerDom = {};
let selectedCurrencyDom = false;
let projectTypeDom = false;
// Entrypoint to be sent with TTREF events.
let entryPoint = 'directoryInline4';
let abTestSkipMilestone = false;
let signupModalRequired;
let isOnMilestoneCreation = false;
let milestoneCreationCancelRedirectUrl = null;
let hireMeModal;
let previousCurrency;

function entryPointToQTS(original) {
  // Some of our ttref entryPoint values have a different QTS mapping. Arg!
  return original === 'directoryInline4' ? 'Directory' : original;
}

const hiremeEvents = {
  /* Tracking events called on form open, successful hire, milestone
   * award/success
   * Please use these instead of hacking additional events inline... :(
   */
  success(data) {
    _ttref.push([
      '_trackEvent',
      'ProjectHireMe',
      'Award',
      'Success',
      'project_id',
      data.project_id,
    ]);
    _lt.push({
      event: 'HireMe Posted',
    });
  },
  quickHireReturnDataReady() {
    _lt.push([
      'user_action',
      {
        section: entryPointToQTS(entryPoint),
        subsection: 'MilestoneCreate_InitialHireMe',
        label: 'form_open',
        action: 'click',
        project_id: quickHireReturnData.project_id,
      },
    ]);
  },
  milestoneClick() {
    _ttref.push([
      '_trackEventNoCookie',
      'HireMe',
      'Initial_Milestone',
      entryPoint,
      'click',
    ]);
    _lt.push([
      'user_action',
      {
        section: entryPointToQTS(entryPoint),
        subsection: 'MilestoneCreate_InitialHireMe',
        label: 'form_submit',
        action: 'click',
        project_id: quickHireReturnData.project_id,
      },
    ]);
  },
  milestoneSuccess() {},
};

function findErrorEl() {
  const errorEl = quickhireFormContainerDom.find(
    '#quickhire-modal-milestone-generic-error, ' +
      '#quickhire-modal-generic-error, ' +
      '#milestone-error',
  );

  return errorEl;
}

function findWarningEl() {
  return quickhireFormContainerDom.find('.quickhire-modal-generic-warning');
}

function displayError(message) {
  const errorEl = findErrorEl();
  errorEl.text(message);
  errorEl.show();
}

function displayWarning(message) {
  const warningEl = findWarningEl();
  warningEl.text(message);
  warningEl.show();
}

function hideError() {
  const errorEl = findErrorEl();
  errorEl.hide();
}

function convertToExchangeRate(amount, prevRate, curRate) {
  /*
   * Exchange rates are all based on USD so convert the previous currency
   * first to USD before changing to user's selected currency
   */
  return Math.round(amount * prevRate / curRate).toFixed(0);
}

function applyExchangeRate(selectedOption) {
  if (previousCurrency === selectedOption.val()) {
    return;
  }

  // Automatically convert the budget amount based on the selected currency
  const budgetAmountDom = quickhireFormContainerDom.find('.budget-amount');
  const previousValueDom = quickhireFormContainerDom.find(
    oneLine`#modalhireme-fixed-multifield-currency
      option[value="${previousCurrency}"]`,
  );
  const prevRate = previousValueDom.attr('exchange-rate');
  const curRate = selectedOption.attr('exchange-rate');
  const budget = budgetAmountDom.val();
  const convertedBudget = convertToExchangeRate(budget, prevRate, curRate);
  budgetAmountDom.val(convertedBudget);
  previousCurrency = selectedOption.val();

  /*
   * Update the default exchange rate for hourly/fixed price if
   * budget amount field value was not yet modified
   */
  const hourlyValue = budgetAmountDom.attr('hourly-value');
  const fixedValue = budgetAmountDom.attr('fixed-value');
  if (hourlyValue !== fixedValue) {
    const convertedFixedValue = convertToExchangeRate(
      fixedValue,
      prevRate,
      curRate,
    );
    const convertedHourlyValue = convertToExchangeRate(
      hourlyValue,
      prevRate,
      curRate,
    );
    budgetAmountDom.attr('fixed-value', convertedFixedValue);
    budgetAmountDom.attr('hourly-value', convertedHourlyValue);
  }
}

function changeMinimumBudget(params) {
  if (selectedCurrencyDom === false) {
    selectedCurrencyDom = quickhireFormContainerDom.find(
      '#modalhireme-fixed-multifield-currency option:selected',
    );
  }
  applyExchangeRate(selectedCurrencyDom);

  const projectTypeDetailDom = params.triggeringElement.closest(
    '.project-type-detail',
  );
  const sign = selectedCurrencyDom.attr('title');

  const minBudget = selectedCurrencyDom.attr(
    `min-${projectTypeDom.val()}-budget`,
  );

  projectTypeDetailDom.find('.budget-currency-sign').html(sign);
  projectTypeDetailDom.find('.min-budget-currency-sign').html(sign);
  projectTypeDetailDom.find('.min-budget-amount').html(minBudget);

  quickhireFormContainerDom.find('.budget-amount').attr('min', minBudget);
  quickhireFormContainerDom.find('.budget-amount').valid();
}

function applyEvents() {
  quickhireFormContainerDom.on('click', '.btn-auth-type', function () {
    const that = jq(this);
    const form = quickhireFormContainerDom.find('.quick-hire-post-form');
    that.find('.user-type').attr('checked', 'checked');
    that.siblings('btn-auth-type').find('.user-type').attr('checked', false);
    if (that.hasClass('returning-user')) {
      form.find('.user-create').hide();
      form.find('.user-return').show();
    } else {
      form.find('.user-return').hide();
      form.find('.user-create').show();
    }
  });

  quickhireFormContainerDom.on('change', '.hoursOption', () => {
    const form = quickhireFormContainerDom.find('.quick-hire-post-form');
    const checkedOption = form.find('.hoursOption:checked');
    if (checkedOption.val() == 'notSure') {
      form.find('.project-workload').attr('disabled', true).val(1);
    } else {
      form.find('.project-workload').removeAttr('disabled');
    }
  });

  quickhireFormContainerDom.on('click', '.hours-option-text', function () {
    const forOption = jq(this).attr('for');
    jq(this).find(`.hoursOption[value=${forOption}]`).trigger('click');
  });

  quickhireFormContainerDom.on('click', '.cancel-quickhire', () => {
    quickhireFormContainerDom.find('.close').trigger('click');
  });

  quickhireFormContainerDom.on('click', '.site_plainterms', () => {
    window.open(
      '/info/plainterms.html',
      '',
      'width=1000, height=600, left=100,top=100,menu=no, ' +
        'toolbar=no,scrollbars=yes,resizable=yes',
    );
    return false;
  });

  quickhireFormContainerDom.on('click', '.site_privacy', () => {
    window.open(
      '/page.php?p=info/privacy',
      '',
      'width=1000, height=600, left=0,top=0,menu=no, ' +
        'toolbar=no,scrollbars=yes,resizable=yes',
    );
    return false;
  });

  projectTypeDom.on('change', function () {
    const formContainer = quickhireFormContainerDom;
    const freelancerId = quickhireFormContainerDom
      .find('.quick-hire-post-form')
      .data('seller-id');

    if (projectTypeDom.val() === 'hourly') {
      formContainer.find('.fixed-detail').hide();
      formContainer.find('.hourly-detail').show();
      formContainer.find('#project_fix_fee_note').hide();
      formContainer.find('#project_hourly_fee_note').show();

      _t.push([
        'user_action',
        {
          section: 'HireMeModal',
          name: 'ToggleToHourly',
          label: 'Per Hour',
          reference: 'freelancer_id',
          reference_id: freelancerId,
        },
      ]);
    } else {
      formContainer.find('.hourly-detail').hide();
      formContainer.find('.fixed-detail').show();
      formContainer.find('#project_fix_fee_note').show();
      formContainer.find('#project_hourly_fee_note').hide();

      _t.push([
        'user_action',
        {
          section: 'HireMeModal',
          name: 'ToggleToFixedPrice',
          label: 'Fixed Price',
          reference: 'freelancer_id',
          reference_id: freelancerId,
        },
      ]);
    }

    const budgetAmountDom = formContainer.find('.budget-amount');
    budgetAmountDom.val(budgetAmountDom.attr(`${projectTypeDom.val()}-value`));
    budgetAmountDom.focusout();

    changeMinimumBudget({
      triggeringElement: jq(this),
    });
  });

  quickhireFormContainerDom.on('click', '.btn-quickhire', () => {
    const form = quickhireFormContainerDom.find('.quick-hire-post-form');
    let hasAuthErrors = false;

    if (
      quickhireFormContainerDom.find('#userTypeReturning').is(':checked') &&
      !userAuthForm.userReturned()
    ) {
      quickhireFormContainerDom
        .find('#post-proj-username, #post-proj-pwd')
        .closest('.control-group')
        .removeClass('success')
        .addClass('error');
      quickhireFormContainerDom
        .find('#post-proj-username')
        .siblings('.help-inline')
        .html(T_('Please check your login details.'));
      hasAuthErrors = true;
    }

    if (quickhireFormContainerDom.find('#userTypeNew').is(':checked')) {
      if (quickhireFormContainerDom.find('#new-email').val() == '') {
        quickhireFormContainerDom
          .find('#new-email')
          .closest('.control-group')
          .removeClass('success')
          .addClass('error');
        quickhireFormContainerDom
          .find('#new-email')
          .siblings('.help-inline')
          .html(T_('This field is required.'));
        hasAuthErrors = true;
      }
      if (quickhireFormContainerDom.find('#new-username').val() == '') {
        quickhireFormContainerDom
          .find('#new-username')
          .closest('.control-group')
          .removeClass('success')
          .addClass('error');
        quickhireFormContainerDom
          .find('#new-username')
          .siblings('.help-inline')
          .html(T_('This field is required.'));
        hasAuthErrors = true;
      }

      if (quickhireFormContainerDom.find('#passwd').val() == '') {
        quickhireFormContainerDom
          .find('#passwd')
          .closest('.control-group')
          .removeClass('success')
          .addClass('error');
        quickhireFormContainerDom
          .find('#passwd')
          .siblings('.help-inline')
          .html(T_('This field is required.'));
        hasAuthErrors = true;
      }
    }

    if (hasAuthErrors) {
      const errorElement = quickhireFormContainerDom
        .find('.control-group.error:first')
        .offset().top;
      jq(document).scrollTop(errorElement - 250);
    }
    if (validateForm(form)) {
      if (thisUser || (!hasAuthErrors && userAuthForm.validateForm())) {
        submitForm(form);
      }
    }
  });

  quickhireFormContainerDom.on(
    'click',
    '.btn-quickhire-create-milestone',
    () => {
      const milestoneForm = quickhireFormContainerDom.find(
        '.quick-hire-milestone-form',
      );
      if (validateMilestoneForm(milestoneForm)) {
        submitMilestoneForm(milestoneForm);
      }
    },
  );

  quickhireFormContainerDom.on('click', '.btn-quickhire-no-milestone', () => {
    const formContainer = quickhireFormContainerDom;
    formContainer
      .find('.btn-quickhire-create-milestone')
      .attr('disabled', true);
    formContainer.find('.btn-quickhire-no-milestone').attr('disabled', true);
  });

  quickhireFormContainerDom.on(
    'change',
    '#modalhireme-fixed-multifield-currency',
    function () {
      selectedCurrencyDom = jq(this).find('option:selected');
      changeMinimumBudget({
        triggeringElement: jq(this),
      });
    },
  );

  quickhireFormContainerDom.on('change', '#autoOpenToBidding', () => {
    changeDescValidateRule();

    const bodyCollapse = jq('.HireMeFormModal-bodyCollapse');
    if (quickhireFormContainerDom.find('#autoOpenToBidding').is(':checked')) {
      bodyCollapse.addClass('is-HireMeFormModal-bodyCollapse-active');
    } else {
      bodyCollapse.removeClass('is-HireMeFormModal-bodyCollapse-active');
    }
  });

  quickhireFormContainerDom.on('change', '.budget-amount', function () {
    jq(this).attr('fixed-value', jq(this).val());
    jq(this).attr('hourly-value', jq(this).val());
  });

  jq.validator.addMethod(
    'checkCraigslistViolation',
    function (value, element) {
      const craigslistTerms = ['craigslist', /(^|\W)cl($|\W)/, 'clist'];
      const violationTerms = [
        'ghost',
        'ghosting',
        'flag',
        'flagging',
        'block',
        'blocking',
        'pva',
        'pvas',
        'phone verified accounts',
        'auto reply',
        'auto respond',
        'post',
        'poster',
        'posting',
        'autopost',
        'autoposter',
        'autoposting',
        'anti-flagging',
        'captcha',
        'captchas',
      ];

      let craigslistCheckResult = false;
      for (let i = craigslistTerms.length - 1; i >= 0; i--) {
        if (jq(element).val().toLowerCase().match(craigslistTerms[i])) {
          craigslistCheckResult = true;
        }
      }
      let violationCheckResult = false;
      for (let j = violationTerms.length - 1; j >= 0; j--) {
        if (jq(element).val().toLowerCase().match(violationTerms[j])) {
          violationCheckResult = true;
        }
      }
      if (craigslistCheckResult && violationCheckResult) {
        jq(this).find('#warning_craigslist_id').show();
        return true;
      }
      quickhireFormContainerDom.find('#warning_craigslist_id').hide();
      return true;
    },
    T_("Please ensure you do not violate Craigslist's TOU."),
  );

  jq.validator.addMethod(
    'checkCopyRightViolation',
    function (value, element) {
      const copyRightViolationTerms = ['clone', 'exact copy'];

      for (let i = copyRightViolationTerms.length - 1; i >= 0; i--) {
        if (jq(element).val().toLowerCase().match(copyRightViolationTerms[i])) {
          jq(this).find('#warning_copyright_id').show();
          return true;
        }
      }
      quickhireFormContainerDom.find('#warning_copyright_id').hide();
      return true;
    },
    T_("Please ensure you do not violate Craigslist's TOU."),
  );

  jq.validator.addMethod(
    'checkFixedPeriod',
    (value, element) => {
      const that = jq(element);

      if (projectTypeDom.val() === 'fixed') {
        const inputAmount = parseInt(that.val(), 10);
        if (isNaN(inputAmount) || inputAmount <= 0) {
          return false;
        }
      }
      return true;
    },
    T_('Please enter a valid period.'),
  );

  jq.validator.addMethod(
    'checkHourlyWorkload',
    (value, element) => {
      const that = jq(element);

      if (projectTypeDom.val() === 'hourly') {
        const inputAmount = parseInt(that.val(), 10);
        if (isNaN(inputAmount) || inputAmount <= 0) {
          return false;
        }
      }
      return true;
    },
    T_('Please enter a valid workload'),
  );

  jq.validator.addMethod(
    'checkBudget',
    (value, element) => {
      const form = quickhireFormContainerDom;
      const that = jq(element);
      const inputAmount = parseInt(that.val(), 10);
      let minBudgetAmount = 0;
      let optionSelected;

      if (projectTypeDom.val() === 'fixed') {
        optionSelected = form.find(
          '.fixed-detail ' +
          '#modalhireme-fixed-multifield-currency option:selected',
        );
        minBudgetAmount = parseInt(optionSelected.attr('min-fixed-budget'), 10);
      } else if (projectTypeDom.val() === 'hourly') {
        optionSelected = form.find(
          oneLine`.hourly-detail #modalhireme-fixed-multifield-currency
          option:selected`,
        );
        minBudgetAmount = parseInt(
          optionSelected.attr('min-hourly-budget'),
          10,
        );
      }

      if (isNaN(inputAmount) || inputAmount < minBudgetAmount) {
        return false;
      }

      return true;
    },
    T_('Please enter a valid budget.'),
  );
}

function changeDescValidateRule() {
  const limit = quickhireFormContainerDom
    .find('#autoOpenToBidding')
    .is(':checked')
    ? 100
    : 10;

  quickhireFormContainerDom.find('.project-descr').rules('remove', 'minlength');
  quickhireFormContainerDom.find('.project-descr').rules('add', {
    minlength: limit,
  });
}

function submitForm(form) {
  const data = {};
  const skills = [];
  const filenames = [];
  const selectedSkills = form.find('.selected_skill');
  const formContainer = quickhireFormContainerDom;
  const filesUploaded = form.find('.uploadFileSelectList option:selected');
  data.csrf_token = getCSRFToken();
  data.type = projectTypeDom.val();

  if (data.type == 'fixed') {
    const fixedDetail = form.find('.fixed-detail');
    data.period = fixedDetail.find('.project-period').val();
    data.currency = fixedDetail
      .find('#modalhireme-fixed-multifield-currency')
      .val();
    data.budget = fixedDetail.find('.budget-amount').val();
  } else if (data.type == 'hourly') {
    const hourlyDetail = form.find('.hourly-detail');
    data.commitment_interval = 'week';
    data.duration = hourlyDetail.find('.project-duration').val();
    data.commitment = hourlyDetail.find('.project-workload').val();
    data.budget = hourlyDetail.find('.budget-amount').val();
    data.currency = hourlyDetail
      .find('#modalhireme-fixed-multifield-currency')
      .val();
  } else {
    return false;
  }

  data.pmb = form.find('#pmb').val();
  if (form.find('#autoOpenToBidding').is(':checked')) {
    data.autoOpenToBidding = 'checked';
  } else {
    data.autoNameAndDescription = true;
  }

  data.name = form.find('.project-name').val();
  data.descr = form.find('.project-descr').val();
  data.sellerId = form.data('seller-id');
  data.sellerUsername = form.data('seller-username');
  data.sourceType = form.data('source-type');
  data.sourceId = form.data('source-id');
  for (let i = 0; i < selectedSkills.length; i++) {
    skills.push(jq(selectedSkills[i]).attr('value'));
  }
  data.skills = skills;
  data.fileset = form.find('[name=fileset]').val();
  for (let j = 0; j < filesUploaded.length; j++) {
    filenames.push(jq(filesUploaded[j]).val());
  }
  data.filename = filenames;

  const authform = quickhireFormContainerDom.find('#nonAuthForm');
  if (!thisUser && authform.find('#userTypeNew').is(':checked')) {
    data.username = authform.find('#new-username').val();
    data.email = authform.find('#new-email').val();
    data.passwd = authform.find('#passwd').val();
    data.passwd1 = data.passwd;
    data.email_notify = true;
  }
  jq.ajax({
    url: '/buyers/onquickhire.php',
    type: 'post',
    data,
    dataType: 'json',
    beforeSend() {
      formContainer.find('.loading-img').show();
      formContainer.find('.btn-quickhire').attr('disabled', true).hide();
      formContainer.find('.cancel-quickhire').attr('disabled', true);
      hideError();
    },
    success(response) {
      hiremeEvents.success(response);
      if (response.status == 'success' && !response.ABTestAutomaticPayment) {
        isOnMilestoneCreation = true;
        let popoverPlacement = 'right';
        upsellUpgrades(response);
        quickhireFormContainerDom.removeClass('quick-hire-form-container');
        popoverPlacement = 'bottom';

        quickHireReturnData = response;
        hiremeEvents.quickHireReturnDataReady();

        milestoneCreationCancelRedirectUrl = quickHireReturnData.url;

        let budget = data.budget;
        if (data.type === 'fixed') {
          budget *= 0.5;
        }

        const quickHireMilestoneFormHTML = _.template(
          quickHireMilestoneFormTemplate,
        )({
          username: data.sellerUsername,
          userID: data.sellerId,
          quickHireReturnData,
          budget,
          type: data.type,
          commitment: data.type == 'hourly' ? data.commitment : false,
          abTestSkipMilestone,

          T_,
          cdn_url,
          uiIcon,
        });
        quickhireFormContainerDom.html(quickHireMilestoneFormHTML);
        quickhireFormContainerDom.find('#milestones-popover').popover({
          placement: popoverPlacement,
          trigger: 'hover',
          animation: true,
          template: '<div class="popover"><div class="arrow"></div>' +
            '<div class="popover-inner">' +
            '<div class="popover-content"><p></p></div></div></div>',
        });
        initMilestoneValidator(
          quickhireFormContainerDom.find('.quick-hire-milestone-form'),
        );
        thisUser = getUserIdfromCookie();

        quickhireFormContainerDom.find('#milestone-not-now').click(() => {
          hireMeModal.hide();
        });

        // Show div to display info about not being able
        // to send the private message
        if (response.pmNotSentMessage) {
          displayWarning(response.pmNotSentMessage);
        }

        quickhireFormContainerDom
          .find('.btn-quickhire-no-milestone')
          .on('click', () => {
            // Because the href in the button on the template
            // does not redirect in some older browsers
            // (looking at you, IE8)
            window.location.href = quickHireReturnData.url;
          });
      } else if (response.url) {
        window.location.href = response.url;
      } else {
        displayError(response.reason);
      }
    },
    error() {
      displayError(T_('Server Error'));
    },
    complete() {
      formContainer.find('.loading-img').hide();
      formContainer.find('.btn-quickhire').removeAttr('disabled').show();
      formContainer.find('.cancel-quickhire').removeAttr('disabled');
    },
  });
}

function getTotal() {
  const quickhire = quickhireFormContainerDom;
  let total = awardUpgrades.getUpgradeTotal();
  if (parseFloat(quickhire.find('.milestone-amount').val())) {
    total += parseFloat(quickhire.find('.milestone-amount').val());
  }
  return total.toFixed(2);
}

function upsellUpgrades(data) {
  const quickhire = quickhireFormContainerDom;
  const that = this;
  const updateTotal = () => {
    quickhire.find('.total-amount').html(getTotal());
  };
  const initPaymentSource = data => {
    const paymentsSourceDetails = {
      currencyDetails: {
        id: data.currency_id,
        sign: data.currency_sign,
        code: data.currency_code,
      },
    };
    paymentsSourceDetails.amount = getTotal();
    paymentSource.init(
      paymentsSourceDetails,
      quickhire.find('#payment-sources-container'),
    );
  };

  const upgradesPrepared = () => {
    awardUpgrades.init();
    initPaymentSource(data);
    updateTotal();
    quickhire.find('.upgrade-checkbox').on('change', updateTotal);
    quickhire.find('.milestone-amount').on('keyup', updateTotal);
  };

  if (typeof data.abTestPrehire !== 'undefined' && data.abTestPrehire) {
    awardUpgrades.prepareUpgradeForPreHireChat(data.currency_id).then(() => {
      upgradesPrepared();
    });
  } else {
    awardUpgrades.prepareUpgradeABTest(data.project_id, true).then(() => {
      upgradesPrepared();
      quickhire
        .off('click', '.btn-quickhire-create-milestone')
        .on('click', '.btn-quickhire-create-milestone', () => {
          const milestoneForm = quickhire.find('.quick-hire-milestone-form');
          if (validateMilestoneForm(milestoneForm)) {
            awardUpgrades.upgradeAndCreateMilestone(
              data,
              milestoneForm,
              submitMilestoneForm,
              that,
            );
          }
        });
    });
  }
}

function submitMilestoneForm(milestoneForm, isPrehireProject = false) {
  // track user confirm to create milestone
  hiremeEvents.milestoneClick();
  const data = {};
  const formContainer = quickhireFormContainerDom;
  data.csrf_token = getCSRFToken();
  data.username = quickHireReturnData.seller_username;
  data.amount = milestoneForm.find('.milestone-amount').val();
  data.project = quickHireReturnData.project_id;
  data.reason = 'partly_payment';
  data.other_reason = milestoneForm.find('.milestone-descr').val();
  data.currency = quickHireReturnData.currency_id;
  // m is useMaster
  data.m = true;

  jq.ajax({
    url: '/ajax/payment/onnewescrow.php',
    type: 'post',
    data,
    headers: { 'X-XSRF-TOKEN': getCSRFToken() },
    dataType: 'json',
    beforeSend() {
      formContainer.find('.loading-img').show();
      formContainer
        .find('.btn-quickhire-create-milestone')
        .attr('disabled', true)
        .hide();
      formContainer.find('.btn-quickhire-no-milestone').attr('disabled', true);
      hideError();
    },
    success(response) {
      if (response.status == 'success') {
        if (!isPrehireProject) {
          quickHireReturnData.url +=
            `&ttref=HireMe_${entryPoint}` +
            '&hireme_initial_milestone_tracking=immediate';
          window.location.href = quickHireReturnData.url;
        } else {
          formContainer.find('.loading-img').hide();
          hireMeModal.hide();
        }
      } else if (response.needDeposit) {
        const uri =
          '/payments/deposit.php?' +
          `milestone_amount=${encodeURIComponent(data.amount)}` +
          `&amount=${encodeURIComponent(
            response.amountDiff ? response.amountDiff : data.amount,
          )}` +
          `&currency=${encodeURIComponent(data.currency)}` +
          `&id=${encodeURIComponent(data.project)}` +
          `&descr=${encodeURIComponent(data.other_reason)}` +
          `&username=${encodeURIComponent(data.username)}` +
          `&bidderId=${encodeURIComponent(quickHireReturnData.seller_id)}` +
          `&refAction=${encodeURIComponent('create_milestone')}` +
          `&ttref=HireMe_${entryPoint}` +
          '&hireme_initial_milestone_tracking=' +
          'success_deposit_redirection' +
          `&paymentItem=${response.paymentItem}` +
          '&paymentDetail=true';
        window.location = uri;
      } else {
        displayError(response.msg);
        formContainer
          .find('.btn-quickhire-create-milestone')
          .removeAttr('disabled')
          .show();
        formContainer
          .find('.btn-quickhire-no-milestone')
          .removeAttr('disabled');
        formContainer.find('.loading-img').hide();
      }
    },
    error() {
      displayError(T_('Server Error'));
      formContainer
        .find('.btn-quickhire-create-milestone')
        .removeAttr('disabled')
        .show();
      formContainer.find('.btn-quickhire-no-milestone').removeAttr('disabled');
      formContainer.find('.loading-img').hide();
    },
    complete() {
      formContainer.find('.loading-img').hide();
    },
  });
}

function createPrehireProject(params, milestoneForm) {
  const that = jq(this);
  const formContainer = quickhireFormContainerDom;
  const initialMilestoneAmount = parseFloat(jq('.milestone-amount').val());
  const data = {
    currency: params.employer.primary_currency.id,
    sellerUsername: params.freelancer.username,
    sellerId: params.freelancer.id,
    type: 'fixed',
    period: 999,
    budget: initialMilestoneAmount,
    isPrehireProject: true,
    csrf_token: getCSRFToken(),
    autoNameAndDescription: true,
    prehireTotalCharge: getTotal(),
    autoSendPMB: params.autoSendPMB,
  };

  jq.ajax({
    url: '/buyers/onquickhire.php',
    type: 'post',
    data,
    dataType: 'json',
    beforeSend() {
      formContainer.find('.loading-img').show();
      formContainer
        .find('.btn-quickhire-create-milestone')
        .attr('disabled', true)
        .hide();
      formContainer.find('.btn-quickhire-no-milestone').attr('disabled', true);
      hideError();
    },
    success(response) {
      if (response.status == 'success') {
        params.deferred.resolve(response.project_id);
        milestoneForm.find('#prehire-created').show();

        const upgradeData = {
          project_id: response.project_id,
          currency_id: response.currency_id,
          seller_id: response.seller_id,
          seller_username: response.seller_username,
          abTestPrehire: true,
        };

        quickHireReturnData = response;

        awardUpgrades.upgradeAndCreateMilestone(
          upgradeData,
          milestoneForm,
          submitMilestoneForm,
          that,
          true,
        );
      } else {
        params.deferred.reject('Failed to create project');
        if (response.reason) {
          displayError(response.reason);
        }
        if (response.url) {
          window.location.href = response.url;
        }
      }
    },
    error() {
      params.deferred.reject('Failed to create project');
      displayError(T_('Server Error'));
    },
    complete() {
      formContainer.find('.loading-img').hide();
      formContainer.find('.btn-quickhire-no-milestone').removeAttr('disabled');
      formContainer
        .find('.btn-quickhire-create-milestone')
        .removeAttr('disabled')
        .show();
    },
  });
}

function validateForm(form) {
  let detailErrors = false;

  if (projectTypeDom.val() === 'hourly') {
    detailErrors = form.find('.fixed-detail .error');
  } else if (projectTypeDom.val() === 'fixed') {
    detailErrors = form.find('.hourly-detail .error');
  }

  if (detailErrors !== false) {
    detailErrors.removeClass('error');
    detailErrors.find('.help-block').html('');
  }

  return form.valid();
}

function validateMilestoneForm(milestoneForm) {
  return milestoneForm.valid();
}

function initMilestoneValidator(milestoneForm) {
  jq.validator.addMethod(
    'checkMilestoneAmount',
    (value, element) => {
      const that = jq(element);
      const inputAmount = parseFloat(that.val());
      if (isNaN(inputAmount) || inputAmount <= 0) {
        return false;
      }
      return true;
    },
    T_('Please enter a valid amount'),
  );

  milestoneForm.validate({
    errorElement: 'span',
    onsubmit: false,
    errorPlacement(error, element) {
      error.appendTo(element.closest('.control-group').find('.help-block'));
    },
    onfocusout(element) {
      if (jq(element).val()) {
        jq(element).valid();
      } else {
        return false;
      }
    },
    highlight(label) {
      jq(label)
        .closest('.control-group')
        .addClass('error is-invalid')
        .removeClass('is-valid');
    },
    success(label) {
      jq(label)
        .closest('.control-group')
        .removeClass('error is-invalid')
        .addClass('is-valid');
    },
    rules: {
      milestoneAmount: {
        checkMilestoneAmount: true,
        min: 0.01,
        required: true,
      },
      milestoneDescr: {
        minlength: 1,
        maxlength: 40,
        required: true,
      },
    },
  });
}

function initValidator(form) {
  form.validate({
    errorElement: 'span',
    onsubmit: false,
    errorPlacement(error, element) {
      element
        .closest('.control-group')
        .find('.help-block, .help-inline')
        .eq(0)
        .html(error);
    },
    onfocusout(element) {
      if (jq(element).val()) {
        jq(element).valid();
      } else {
        return false;
      }
    },
    highlight(label) {
      jq(label).closest('.control-group').addClass('error');
    },
    success(label) {
      jq(label).closest('.control-group').removeClass('error');
    },
    rules: {
      project_name: {
        minlength: 10,
        remote: '/ajax/postproject/check-project-name.php',
        required: true,
      },
      projectDescr: {
        minlength: form.find('#autoOpenToBidding').is(':checked') ? 100 : 10,
        maxlength: 4000,
        checkCraigslistViolation: true,
        required: true,
      },
      pmb: {
        minlength: 10,
        maxlength: 4000,
        checkCraigslistViolation: true,
        required: true,
      },
      fixedBudget: {
        checkBudget: true,
      },
      fixedPeriod: {
        checkFixedPeriod: true,
      },
      hourlyWorkload: {
        checkHourlyWorkload: true,
      },
      hourlyRate: {
        checkBudget: true,
      },
      budget: {
        checkBudget: true,
      },
    },
    messages: {
      project_name: {
        remote: T_('You already have a project with this name.'),
      },
    },
  });
}

function initFileUpload(params) {
  // First param was never used in this module. Add in support if you need
  // it.
  fileupload.init(undefined, params);
}

function initUserAuthForm() {
  userAuthForm.init({
    noConfirmPassword: true,
    signupModalRequired,
  });
}

function initTooltips(form) {
  form.find('#autoOpenToBidding-tip').popover({
    placement: 'top',
    template: '<div class="popover"><div class="arrow"></div>' +
      '<div class="popover-inner" style="width:400px">' +
      '<h3 class="popover-title"></h3><div class="popover-content"><p></p>' +
      '</div></div></div>',
  });

  form.find('#project-type-tip').popover({
    placement: 'top',
    content: `${T_(
        '<b>Fixed Price</b>: Ideal when you have a project with a well ' +
        'defined scope and detailed project description. ',
      )}` +
      `${T_(
        'Project fees are charged when the freelancer accepts the project.',
      )}<br><br>` +
      `${T_(
        '<b>Hourly Projects</b>: Recommended when you want to hire a ' +
        'freelancer for ongoing work. ',
      )}` +
      `${T_(
        'The project description can be less well defined. Project fees are' +
        ' charged on each payment to the freelancer.',
      )}`,
    trigger: 'hover',
    html: true,
  });

  form.find('.quick-hire-post-form-tip').each(function () {
    const that = jq(this);
    that.popover({
      content: that.data('content'),
      title: that.data('title'),
      placement: 'top',
      trigger: 'hover',
      html: true,
    });
  });
}

function initForm(hireMeParams, fileUploadParams, ttSource) {
  if (!document.getElementById('modal-hire-me')) {
    document.body.insertAdjacentHTML('beforeend', hiremeModalContainerTemplate);
  }

  signupModalRequired = hireMeParams.signupModalRequired;

  const initCallback = () => {
    if (
      typeof window.abTestHiremeMilestone !== 'undefined' &&
      window.abTestHiremeMilestone
    ) {
      abTestSkipMilestone = window.abTestHiremeMilestone;
    }

    if (ttSource) {
      entryPoint = ttSource;
    }

    // Sensible fallback values
    // Consider using
    // /ajax/getProjectSuggestedBudget.php?user_id=...
    hireMeParams = jq.extend(
      {
        fixed_budget: 250,
        hourlyrate: 10,
        default_min: 10,
        daysDuration: 3,
      },
      hireMeParams,
    );

    feeNotes =
      feeNotes ||
      (thisUser == hireMeParams.userID ? false : hireMeParams.projectFee);
    const quickHireFormHTML = _.template(quickHireFormTemplate)({
      username: hireMeParams.username.trim(),
      userID: hireMeParams.userID,
      hourlyrate: hireMeParams.hourlyrate,
      is_online: hireMeParams.is_online,
      email: hireMeParams.email || '',
      budgets: budgetsCache,
      min_fixed_budget: hireMeParams.fixed_budget,
      default_min: hireMeParams.default_min,
      durations: durationsCache,
      days_duration: hireMeParams.daysDuration,
      autoProjectName: '',
      thisUser,
      feeNotes,
      isNewUser: hireMeParams.isNewUser,
      project_title: hireMeParams.projectTitle || '',
      project_description: hireMeParams.projectDescription || '',
      show_hireme_promotion_copy: hireMeParams.showHireMePromotionCopy,
      sourceType: hireMeParams.sourceType,
      sourceId: hireMeParams.sourceId,
      T_,
      uiIcon,
      cdn_url,
      locale: {
        fileupload: {
          errors: {
            maxFileSize: T_('Your file is bigger than 20 MBs.'),
            minFileSize: T_('File is too small'),
            acceptFileTypes: T_('Filetype not allowed'),
            maxNumberOfFiles: T_('Max number of files exceeded'),
            uploadedBytes: T_('Uploaded bytes exceed file size'),
            emptyResult: T_('Empty file upload result'),
          },
          error: T_('Error'),
          start: T_('Start'),
          cancel: T_('Cancel'),
          destroy: T_('Delete'),
        },
      },
    });

    previousCurrency = jq(quickHireFormHTML)
      .find('#modalhireme-fixed-multifield-currency option:selected')
      .val();

    const quickHireFormHeaderHTML = _.template(quickHireFormTemplateHeader)({
      T_,
      username: hireMeParams.username.trim(),
    });

    const quickHireForm = jq('#modal-hire-me-new');

    if (quickHireForm.length) {
      quickHireForm.remove();
    }

    hireMeModal = new Modal(
      'modal-hire-me-new',
      {
        body: quickHireFormHTML,
        header: quickHireFormHeaderHTML,
      },
      {
        extraClass: 'quick-hire-form-container',
        backdropClose: false,
        onHide: () => {
          if (isOnMilestoneCreation) {
            qtsp.push({
              eventName: 'user_action',
              section: 'HireMe',
              subsection: 'CreateMilestoneModal',
              label: 'Close',
            });

            window.location.href = milestoneCreationCancelRedirectUrl;
          }
        },
      },
    );

    const formContainer = jq('#quick-hire-form');

    fileUploadParams = jq.extend(
      {
        domEl: formContainer,
        uploadTemplate: 'quickhire-template-upload',
        downloadTemplate: 'quickhire-template-download',
      },
      fileUploadParams,
    );

    quickhireFormContainerDom = formContainer;
    thisUser = getUserIdfromCookie();

    hireMeModal.show();

    projectTypeDom = formContainer.find('#project-type');

    applyEvents();
    initAutoRepost(hireMeParams);
    const form = formContainer.find('.quick-hire-post-form');
    initValidator(form);
    initTooltips(form, hireMeParams);
    initFileUpload(fileUploadParams);
    showFixedBudgetSuggestion(hireMeParams.userID, hireMeParams.username, form);
    initFacebookCallback(form);

    if (!thisUser) {
      initUserAuthForm();
    }
    selectBudgetType(hireMeParams, projectTypeDom);

    if (thisUser) {
      let variation = 'control';

      if (hireMeParams.currencyId) {
        variation = 'test';

        quickhireFormContainerDom
          .find('#modalhireme-fixed-multifield-currency')
          .val(hireMeParams.currencyId)
          .trigger('change');
      }

      ABTestApi.testSubjectActivate(
        'FOT-2551-hm_currency_conversion_restart',
        variation,
      );
    }
  };

  const initHourlyFormCallBack = () => {
    if (
      typeof window.abTestHiremeMilestone !== 'undefined' &&
      window.abTestHiremeMilestone
    ) {
      abTestSkipMilestone = window.abTestHiremeMilestone;
    }

    if (ttSource) {
      entryPoint = ttSource;
    }

    hireMeParams = jq.extend(
      {
        hourlyrate: 10,
        default_min: 10,
        daysDuration: 3,
        budget_period: 'hourly',
      },
      hireMeParams,
    );

    feeNotes =
      feeNotes ||
      (thisUser == hireMeParams.userID ? false : hireMeParams.projectFee);
    const quickHireFormHTML = _.template(hourlyFormTemplate)({
      username: hireMeParams.username.trim(),
      userID: hireMeParams.userID,
      hourlyrate: hireMeParams.hourlyrate,
      is_online: hireMeParams.is_online,
      email: hireMeParams.email || '',
      budgets: budgetsCache,
      min_fixed_budget: hireMeParams.fixed_budget,
      default_min: hireMeParams.default_min,
      durations: durationsCache,
      days_duration: hireMeParams.daysDuration,
      autoProjectName: '',
      thisUser,
      feeNotes,
      isNewUser: hireMeParams.isNewUser,
      project_title: hireMeParams.projectTitle || '',
      project_description: hireMeParams.projectDescription || '',
      show_hireme_promotion_copy: hireMeParams.showHireMePromotionCopy,
      sourceType: hireMeParams.sourceType,
      sourceId: hireMeParams.sourceId,
      T_,
      uiIcon,
      cdn_url,
      locale: {
        fileupload: {
          errors: {
            maxFileSize: T_('Your file is bigger than 20 MBs.'),
            minFileSize: T_('File is too small'),
            acceptFileTypes: T_('Filetype not allowed'),
            maxNumberOfFiles: T_('Max number of files exceeded'),
            uploadedBytes: T_('Uploaded bytes exceed file size'),
            emptyResult: T_('Empty file upload result'),
          },
          error: T_('Error'),
          start: T_('Start'),
          cancel: T_('Cancel'),
          destroy: T_('Delete'),
        },
      },
    });

    previousCurrency = jq(quickHireFormHTML)
      .find('#modalhireme-fixed-multifield-currency option:selected')
      .val();

    const quickHireFormHeaderHTML =
      _.template(quickHireHourlyFormTemplateHeader)({
        T_,
        username: hireMeParams.username.trim(),
      });

    const quickHireForm = jq('#modal-hire-me-new');

    if (quickHireForm.length) {
      quickHireForm.remove();
    }

    hireMeModal = new Modal(
      'modal-hire-me-new',
      {
        body: quickHireFormHTML,
        header: quickHireFormHeaderHTML,
      },
      {
        extraClass: 'quick-hire-form-container',
        onHide: () => {
          if (isOnMilestoneCreation) {
            qtsp.push({
              eventName: 'user_action',
              section: 'HireMe',
              subsection: 'CreateMilestoneModal',
              label: 'Close',
            });

            window.location.href = milestoneCreationCancelRedirectUrl;
          }
        },
      },
    );

    const formContainer = jq('#quick-hire-form');

    fileUploadParams = jq.extend(
      {
        domEl: formContainer,
        uploadTemplate: 'quickhire-template-upload',
        downloadTemplate: 'quickhire-template-download',
      },
      fileUploadParams,
    );

    quickhireFormContainerDom = formContainer;
    thisUser = getUserIdfromCookie();

    hireMeModal.show();

    projectTypeDom = formContainer.find('#project-type');

    applyEvents();
    initAutoRepost(hireMeParams);
    const form = formContainer.find('.quick-hire-post-form');
    initValidator(form);
    initTooltips(form, hireMeParams);
    initFileUpload(fileUploadParams);
    showFixedBudgetSuggestion(hireMeParams.userID, hireMeParams.username, form);
    initFacebookCallback(form);

    if (!thisUser) {
      initUserAuthForm();
    }
    selectBudgetType(hireMeParams, projectTypeDom);

    if (thisUser) {
      let variation = 'control';

      if (hireMeParams.currencyId) {
        variation = 'test';

        quickhireFormContainerDom
          .find('#modalhireme-fixed-multifield-currency')
          .val(hireMeParams.currencyId)
          .trigger('change');
      }

      ABTestApi.testSubjectActivate(
        'FOT-2551-hm_currency_conversion_restart',
        variation,
      );
    }
  };

  const initPrehireMilestoneFormCallBack = () => {
    const minBudgetForCurrency = _.find(
      budgetsCache,
      budget =>
        budget.vars.currency.id == hireMeParams.employer.primary_currency.id,
    );

    const prehireMilestoneFormHTML = _.template(
      quickHireMilestoneFormTemplate,
    )({
      abTestPrehire: hireMeParams.abTestPrehire,
      username: hireMeParams.freelancer.username,
      currency: hireMeParams.employer.primary_currency,
      budget: minBudgetForCurrency.vars.budgets.fixed[0].min_sum,
      abTestSkipMilestone,
      T_,
      cdn_url,
    });

    if (typeof hireMeModal === 'undefined') {
      hireMeModal = new Modal('modal-chat-project-new', {
        body: prehireMilestoneFormHTML,
      });
    } else {
      jq('#modal-chat-project-new')
        .find('.Modal-body')
        .html(prehireMilestoneFormHTML);
    }

    quickhireFormContainerDom = jq('.milestone-modal-wrapper');

    const milestoneForm = quickhireFormContainerDom.find(
      '.quick-hire-milestone-form',
    );

    initMilestoneValidator(quickhireFormContainerDom.find(milestoneForm));

    quickhireFormContainerDom.on(
      'click',
      '.btn-quickhire-create-milestone',
      () => {
        if (validateMilestoneForm(milestoneForm)) {
          createPrehireProject(hireMeParams, milestoneForm);
        }
      },
    );

    const upsellData = {
      abTestPrehire: hireMeParams.abTestPrehire,
      currency_id: hireMeParams.employer.primary_currency.id,
      currency_sign: hireMeParams.employer.primary_currency.sign,
      currency_code: hireMeParams.employer.primary_currency.code,
    };
    upsellUpgrades(upsellData);

    hireMeModal.show();
  };

  let callback;
  if (hireMeParams.abTestPrehire) {
    callback = initPrehireMilestoneFormCallBack;
  } else if (hireMeParams.hourlyOnly) {
    callback = initHourlyFormCallBack;
  } else {
    callback = initCallback;
  }

  if (!budgetsCache.length || !durationsCache.length) {
    lazyLoadBudgets(callback);
  } else {
    callback();
  }

  if (hireMeParams.abTestPrehire) {
    return hireMeParams.deferred.promise;
  }
}

function initFacebookCallback(form) {
  form.bind('integrateForm', integrateForm);
  form.bind('welcomeFacebook', welcomeFacebook);
}

function selectBudgetType(hireMeParams, projectTypeDom) {
  if (
    typeof hireMeParams === 'undefined' ||
    typeof hireMeParams.budget_period === 'undefined' ||
    typeof projectTypeDom === 'undefined'
  ) {
    // Do nothing
  } else if (hireMeParams.budget_period === 'hourly') {
    projectTypeDom.val('hourly').trigger('change');
  } // defaults to fixed
}

function integrateForm() {
  const fbBsIdentifier = quickhireFormContainerDom.find(
    '.fb-bs-identifier .modal-body',
  );
  quickhireFormContainerDom
    .find('#fb_place_holder')
    .html(fbBsIdentifier.clone(true, true));
  quickhireFormContainerDom.find('#fb_place_holder').siblings().hide();
  quickhireFormContainerDom
    .find('#fb_place_holder .btn-large')
    .removeClass('btn-large');
  // pre-select user type
  quickhireFormContainerDom
    .find('#looking_for_label')
    .parents('.control-group')
    .hide();
  quickhireFormContainerDom.find('#looking_to_hire').prop('checked', true);
}

function welcomeFacebook(event, newuser, welcomeCallback) {
  welcomeCallback(newuser, getUserDetailsCallback);
}

function getUserDetailsCallback(isNewUser, response) {
  const logoHTML =
    `<img src='${response.logo_url}' ` +
    `alt='${response.logoname}' ` +
    "border=0 width=80 height=80 style='margin-right: 20px;'>";
  quickhireFormContainerDom.find('#loggedinLogo').html(logoHTML);
  if (isNewUser) {
    // hide 'back'
    quickhireFormContainerDom
      .find('#loggedinWelcome h1')
      .html(T_('Welcome <span id="loggedinFullname"></span>!'));
  } else {
    quickhireFormContainerDom
      .find('#loggedinWelcome h1')
      .html(T_('Welcome back <span id="loggedinFullname"></span>!'));
  }

  quickhireFormContainerDom.find('#loggedinFullname').html(response.name);
  quickhireFormContainerDom.find('#headerTopLoggedIn').fadeIn('slow');
  quickhireFormContainerDom.find('#nonAuthForm').hide();
  quickhireFormContainerDom.find('#fb_place_holder').remove();
  thisUser = getUserIdfromCookie();
}

function initAutoRepost(params) {
  jq.ajax({
    url: '/ajax/hireme/tickAutoRepost.php',
    type: 'post',
    dataType: 'json',
    data: {
      freelancerId: params.userID,
      csrf_token: getCSRFToken(),
    },
    success(resp) {
      if (resp.status == 'success') {
        if (resp.tickAutoRepost) {
          changeDescValidateRule();
        }
      }
    },
  });
}

function showFixedBudgetSuggestion(userId, userName, form) {
  let fixedBudgetSuggestion;
  const initialFixBudgetSuggetion = () => {
    if (typeof fixedBudgetSuggestion !== 'undefined') {
      const content = T_(
        '<%= userName %> has accepted similar projects for ' +
          '$<%= fixedBudgetSuggestion %> USD',
        {
          userName,
          fixedBudgetSuggestion,
        },
      );
      jq(form).find('#qh_fixed_budget').popover({
        title: T_('Not sure on the Budget?'),
        content,
        trigger: 'manual',
        placement: 'bottom',
        html: true,
      });
      // prefill the budget
      jq(form).find('#qh_fixed_budget').val(fixedBudgetSuggestion);
    }
  };
  const showFixBudgetSuggestion = () => {
    jq(form).find('#qh_fixed_budget').popover('show');
  };
  const hideFixBudgetSuggestion = () => {
    jq(form).find('#qh_fixed_budget').popover('hide');
  };
  const toggleFixBudgetSuggestion = () => {
    jq(form).find('#qh_fixed_budget:visible').popover('toggle');
  };

  jq.ajax({
    url: `/ajax/hireme/getFixedProjectBudgetSuggestion.php?user_id=${userId}`,
    dataType: 'json',
    success(resp) {
      if (
        resp.status == 'success' &&
        resp.result != null &&
        resp.result < 500 &&
        resp.result > 300
      ) {
        fixedBudgetSuggestion = resp.result;
        initialFixBudgetSuggetion();
      }
    },
  });

  jq(form).find('#qh_fixed_budget').focus(showFixBudgetSuggestion);
  jq(form).find('#qh_fixed_budget').blur(hideFixBudgetSuggestion);
  jq(form).find('.fixed').parent().click(toggleFixBudgetSuggestion);
  jq(form).find('.hourly').parent().click(hideFixBudgetSuggestion);
}

function lazyLoadBudgets(callback) {
  jq.ajax({
    url: '/ajax/buyer/load-post-project-params.php',
    dataType: 'json',
    success(response) {
      budgetsCache = jq.map(response.budgets, key => [key]);
      durationsCache = response.durations;
      callback();
    },
  });
}

export default {
  initForm,
};
